import React, { useMemo } from 'react';
import { useIntl } from "react-intl";
import { deployVersion } from '@/util/stringUtil';
import { compareDate, UTCToLocalString } from "@/util/dateUtil";
import { usePaginatedScenarioDeploy } from "@/query/deployment";
import { useScenarioId, useModal } from "@/hook";
import Button from "@/component/Button";
import { DocumentSearchIcon, DeploymentIcon } from "@/assets/svgs/icons";

import DeployModal, { DEPLOY_MODAL_ID } from "./component/DeployModal";
import DeploymentDiffModal, { DEPLOYMENT_DIFF_MODAL_ID } from './component/DeployDiffModal';


const MAX_DEPLOYMENT_COUNT = 20;

const DeploymentView: React.FC = () => {
    const intl = useIntl();
    const scenarioId = useScenarioId();
    const modal = useModal();

    const { data: scenarioDeployPage } = usePaginatedScenarioDeploy(scenarioId, 0, MAX_DEPLOYMENT_COUNT);

    const latestDeployId = useMemo(() => {
        if (!scenarioDeployPage?.content.length)
            return -1;

        const deployments = [...scenarioDeployPage.content];

        deployments.sort((a, b) => {
            if (!a.deployed_at || !b.deployed_at || a.deployed_at === b.deployed_at) {
                return compareDate(a.created_at, b.created_at, "desc");
            }
            return compareDate(a.deployed_at, b.deployed_at, "desc");
        });

        return deployments[0].id;
    }, [scenarioDeployPage?.content]);


    return (<>
        <DeployModal />
        <DeploymentDiffModal />

        <main className="scenario-content-layout">
            <div className="w-md-page h-full flex flex-col gap-2">
                <div className="ml-auto py-0.5">
                    <Button variant="text"
                            onClick={() => modal.open(DEPLOY_MODAL_ID)}
                    >
                        <DeploymentIcon className="size-4"
                                        aria-hidden="true" />
                        {intl.formatMessage({ id: "i06002" })}
                    </Button>
                </div>

                <div role="table"
                     aria-label={intl.formatMessage({ id: "i06007" })}
                     className="selection-table"
                >
                    <div role="rowgroup"
                         className="selection-table-thead grid-table-deployment"
                    >
                        <div role="columnheader"
                             className="selection-table-th pl-[25px]"
                        >
                            {intl.formatMessage({ id: "i06007" })}
                        </div>
                        <div role="columnheader"
                             className="selection-table-th pl-[15px]"
                        >
                            {intl.formatMessage({ id: "i06008" })}
                        </div>
                        <div role="columnheader"
                             className="selection-table-th pl-[15px]"
                        >
                            {intl.formatMessage({ id: "i06009" })}
                        </div>
                        <div role="columnheader"
                             className="selection-table-th pl-[15px]"
                        >
                            {intl.formatMessage({ id: "i06010" })}
                        </div>
                        <div role="columnheader"
                             className="selection-table-th pr-[25px]"
                        >
                            {intl.formatMessage({ id: "i06015" })}
                        </div>
                    </div>
                    <div role="rowgroup"
                         className="selection-table-tbody"
                    >
                        {scenarioDeployPage?.content.map(deploy => (
                            <div key={deploy.id}
                                 role="row"
                                 className="selection-table-tr grid-table-deployment"
                            >
                                <div role="cell"
                                     className="selection-table-td pl-[25px] flex items-center gap-2"
                                >
                                    <div className="truncate">
                                        {deployVersion(deploy.version)}
                                    </div>
                                    {deploy.id === latestDeployId && (
                                        <div role="status"
                                             aria-label={intl.formatMessage({ id: "i06011" })}
                                             className="badge-info text-[14px] font-[500]"
                                        >
                                            {intl.formatMessage({ id: "i06011" })}
                                        </div>
                                    )}
                                </div>
                                <div role="cell"
                                     className="selection-table-td pl-[15px] number-cell"
                                >
                                    {UTCToLocalString(deploy.created_at, "YYYY-MM-DD HH:mm")}
                                </div>
                                <div role="cell"
                                     className="selection-table-td pl-[15px]"
                                >
                                    {deploy.actor
                                        ? `${deploy.actor.name} (${deploy.actor.email})`
                                        : "-"}
                                </div>
                                <div role="cell"
                                     className="selection-table-td pl-[15px] truncate"
                                >
                                    {deploy.description ?? ""}
                                </div>
                                <div role="cell"
                                     className="selection-table-td pr-[25px] w-full flex justify-center"
                                >
                                    <button aria-label={`${deployVersion(deploy.version)} ${intl.formatMessage({ id: "i06015" })}`}
                                            onClick={() => modal.open(DEPLOYMENT_DIFF_MODAL_ID, deploy.id)}
                                    >
                                        <DocumentSearchIcon className="size-4.5 text-black-500"
                                                            aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {!scenarioDeployPage?.content.length && (
                            <div className="empty-notice"
                                 data-testid="empty-notice"
                            >
                                {intl.formatMessage({ id: "i06012" })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    </>);
};


export default DeploymentView;
